import { Injectable } from '@angular/core';
import { FeatureState } from '@modeso/aumico-lib-core-fe';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as fromArTemplatestAction from '../actions/annual-report-templates.actions';
import { arTemplateTranslationsActions } from '../actions/annual-report-templates.actions';
import { GetAnnualReportTemplateTranslation } from '../dtos/get-annual-report-template-translation.dto';
import { UpdateAnnualReportTemplateTranslation } from '../dtos/update-annual-report-template-translation.dto';
import { IAnnualReportTemplateDTO } from '../interfaces/annual-report-template-dto.interface';
import { IARTemplateFilterCriteria } from '../interfaces/annual-report-template-filter-criteria.interface';
import * as fromFeature from '../reducer/annual-report-templates.reducer';
import { UpdateAnnualReportTemplateBalanceTaxonomyIdDto } from '../types/update-annual-report-template-balance-taxonomy-dto.type';
import { UpdateAnnualReportTemplateIncomeTaxonomyIdDto } from '../types/update-annual-report-template-income-taxonomy-dto';
import { UpdateAnnualReportTemplateNotesTemplateIdDto } from '../types/update-annual-report-template-notes-template-dto.type';
import { UpdateAnnualReportTemplatePdfTemplateIdDto } from '../types/update-annual-report-template-pdf-template-dto.type';
import { UpdateAnnualReportTemplateSettingsDto } from '../types/update-annual-report-template-settings-dto.type';
import { IAnnualReportTemplatesProvider } from './annual-report-templates.provider.interface';

@Injectable({
    providedIn: 'root',
})
export class AnnualReportTemplatesProvider implements IAnnualReportTemplatesProvider {
    constructor(private readonly store: Store<FeatureState>) {}

    public createArTemplate(arTemplate: IAnnualReportTemplateDTO): void {
        this.store.dispatch(
            fromArTemplatestAction.annualReportTemplateActions.createArTemplate({ payload: arTemplate }),
        );
    }

    public getAllArTemplates$(): Observable<IAnnualReportTemplateDTO[]> {
        return this.store.pipe(select(fromFeature.SelectAllArTemplates));
    }

    public dispatchArTemplatesList(): void {
        this.store.dispatch(fromArTemplatestAction.annualReportTemplateActions.getAllArTemplates());
    }

    public dispatchDeleteArTemplate(id: string): void {
        this.store.dispatch(fromArTemplatestAction.annualReportTemplateActions.deleteArTemplate({ payload: id }));
    }

    public dispatchUpdateArTemplate(arTemplate: IAnnualReportTemplateDTO): void {
        this.store.dispatch(
            fromArTemplatestAction.annualReportTemplateActions.updateArTemplate({ payload: arTemplate }),
        );
    }

    public dispatchGetArTemplateById(id: string): void {
        this.store.dispatch(fromArTemplatestAction.annualReportTemplateActions.getArTemplateById({ payload: id }));
    }

    public getSelectedArTemplateById$(): Observable<IAnnualReportTemplateDTO> {
        return this.store.pipe(select(fromFeature.SelectSelectedArTemplate));
    }

    public setArTemplateSpecifications(arTemplateSpecifications: IAnnualReportTemplateDTO): void {
        this.store.dispatch(
            fromArTemplatestAction.annualReportTemplateActions.setArTemplateSpecifications({
                payload: arTemplateSpecifications,
            }),
        );
    }

    public setArBalanceSheetTaxonomy(
        arTemplateBalanceSheetTaxonomy: UpdateAnnualReportTemplateBalanceTaxonomyIdDto,
    ): void {
        this.store.dispatch(
            fromArTemplatestAction.annualReportTemplateActions.setArBalanceSheetTaxonomy({
                payload: arTemplateBalanceSheetTaxonomy,
            }),
        );
    }

    public setArIncomeStatementTaxonomy(
        arTemplateIncomeStatementTaxonomy: UpdateAnnualReportTemplateIncomeTaxonomyIdDto,
    ): void {
        this.store.dispatch(
            fromArTemplatestAction.annualReportTemplateActions.setArIncomeStatementTaxonomy({
                payload: arTemplateIncomeStatementTaxonomy,
            }),
        );
    }

    public setArPdfInformation(arTemplatePdfInformation: UpdateAnnualReportTemplatePdfTemplateIdDto): void {
        this.store.dispatch(
            fromArTemplatestAction.annualReportTemplateActions.setPdfInformation({ payload: arTemplatePdfInformation }),
        );
    }

    public setArNotesTemplate(arTemplateNotesTemplate: UpdateAnnualReportTemplateNotesTemplateIdDto): void {
        this.store.dispatch(
            fromArTemplatestAction.annualReportTemplateActions.setArNotesTemplate({ payload: arTemplateNotesTemplate }),
        );
    }

    public setArTemplateSettings(arTemplateSettings: UpdateAnnualReportTemplateSettingsDto): void {
        this.store.dispatch(
            fromArTemplatestAction.annualReportTemplateActions.setArTemplateSettings({ payload: arTemplateSettings }),
        );
    }

    public getArTemplateSpecifications$(): Observable<IAnnualReportTemplateDTO> {
        return this.store.pipe(select(fromFeature.SelectSelectedArTemplate));
    }

    public getBalanceSheetTaxonomy$(): Observable<IAnnualReportTemplateDTO> {
        return this.store.pipe(select(fromFeature.SelectSelectedArTemplate));
    }

    public getIncomeStatementTaxonomy$(): Observable<IAnnualReportTemplateDTO> {
        return this.store.pipe(select(fromFeature.SelectSelectedArTemplate));
    }

    public getPdfInformation$(): Observable<IAnnualReportTemplateDTO> {
        return this.store.pipe(select(fromFeature.SelectSelectedArTemplate));
    }

    public getNotesTemplate$(): Observable<IAnnualReportTemplateDTO> {
        return this.store.pipe(select(fromFeature.SelectSelectedArTemplate));
    }

    public downloadDefaultPdfTemplate(): void {
        this.store.dispatch(fromArTemplatestAction.annualReportTemplateActions.downloadDefaultPdfTemplate());
    }

    public downloadDefaultHgbPdfTemplate(): void {
        this.store.dispatch(fromArTemplatestAction.annualReportTemplateActions.downloadDefaultHgbPdfTemplate());
    }

    public downloadPdfTemplate(id: string): void {
        this.store.dispatch(fromArTemplatestAction.annualReportTemplateActions.downloadPdfTemplate({ payload: id }));
    }

    public uploadPdfTemplate(file: any): void {
        this.store.dispatch(fromArTemplatestAction.annualReportTemplateActions.uploadPdfTemplate({ payload: file }));
    }

    public resetSelectedArTemplate(): void {
        this.store.dispatch(fromArTemplatestAction.annualReportTemplateActions.resetSelectedArTemplate());
    }

    public dispatchGetArTemplatesByFilter(filterCriteria: IARTemplateFilterCriteria): void {
        this.store.dispatch(
            fromArTemplatestAction.annualReportTemplateActions.getArTemplatesByFilter({ payload: filterCriteria }),
        );
    }
    public dispatchGetArTemplateLocale(id: string): void {
        this.store.dispatch(fromArTemplatestAction.annualReportTemplateActions.getArTemplateLocales({ payload: id }));
    }

    public getArTemplateLocale$(): Observable<string[]> {
        return this.store.pipe(select(fromFeature.selectLocales));
    }

    getTranslations$(): Observable<GetAnnualReportTemplateTranslation[]> {
        return this.store.pipe(select(fromFeature.selectTranslations));
    }

    public dispatchGetTranslations(locales: string[], untranslatedOnly: boolean): void {
        this.store.dispatch(arTemplateTranslationsActions.getTranslations({ locales, untranslatedOnly }));
    }

    public dispatchUpdateTranslations(translations: UpdateAnnualReportTemplateTranslation[]): void {
        this.store.dispatch(arTemplateTranslationsActions.updateTranslations({ payload: translations }));
    }
}
